import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import type { Ablehnungsgrund, Ergebnis } from '../lib/warteschlangen'
import { ApiFehler } from '../lib/api'
import { etwasEntschieden } from '../lib/aktualisierung'
import Begruendung from './Begruendung'

/**
 * Ein Stapel, den man von oben nach unten abarbeitet.
 *
 * Tastaturbedienung, weil man Stapel abarbeitet (§10):
 *   j / ↓   nächster Vorgang
 *   k / ↑   voriger
 *   a       genehmigen
 *   r       ablehnen (öffnet das Begründungsfeld)
 *   Escape  schließt
 *
 * Nach jeder Aktion steht da, **was** passiert ist — einschließlich der
 * Punkte, die dabei geflossen sind.
 */

export interface StapelAktion<T> {
  freigeben: (eintrag: T) => Promise<Ergebnis>
  /**
   * `grundCode` ist `null`, wo keiner hingehört — bei den beiden
   * Meldungsschlangen. Dort wird kein Beitrag abgelehnt, sondern
   * festgestellt, dass eine Meldung nicht zutrifft.
   */
  ablehnen: (eintrag: T, grundCode: Ablehnungsgrund | null) => Promise<Ergebnis>
  /** Der Satz, der nach einer Aktion oben steht. */
  bericht: (eintrag: T, ergebnis: Ergebnis, art: 'frei' | 'ab') => string
}

interface Eigenschaften<T> {
  titel: string
  eintraege: T[]
  schluessel: (eintrag: T) => string
  /** Kopfzeile eines Eintrags in der Liste links. */
  zeile: (eintrag: T, ausgewaehlt: boolean) => ReactNode
  /** Die ganze Ansicht rechts. */
  inhalt: (eintrag: T) => ReactNode
  aktion: StapelAktion<T>
  /** Beschriftung der Hauptaktion, je Schlange verschieden. */
  freigebenText?: string
  ablehnenText?: string
  /** Nach einer Entscheidung neu laden. */
  neuLaden: () => void
  leerText: string
  /**
   * Die Ablehnungsgründe, die zu dieser Warteschlange passen. Leer heißt:
   * Hier gehört keiner hin, der Dialog fragt nur nach der internen Notiz.
   */
  gruende: Ablehnungsgrund[]
  /**
   * Wie viele insgesamt warten. Die Liste ist gedeckelt — steht hier eine
   * größere Zahl als Einträge da sind, muss das sichtbar sein, sonst hält man
   * die Schlange für kürzer als sie ist.
   */
  gesamt?: number
}

export default function Stapel<T>({
  titel,
  eintraege,
  schluessel,
  zeile,
  inhalt,
  aktion,
  freigebenText = 'Freigeben',
  ablehnenText = 'Ablehnen',
  neuLaden,
  leerText,
  gruende,
  gesamt,
}: Eigenschaften<T>) {
  const [index, setIndex] = useState(0)
  const [fragtGrund, setFragtGrund] = useState(false)
  const [laeuft, setLaeuft] = useState(false)
  const [bericht, setBericht] = useState<string | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const listeRef = useRef<HTMLUListElement>(null)

  const aktuell = eintraege[Math.min(index, eintraege.length - 1)]

  // Schrumpft die Liste unter den Zeiger, rutscht er mit.
  useEffect(() => {
    if (index > 0 && index >= eintraege.length) setIndex(eintraege.length - 1)
  }, [eintraege.length, index])

  const entscheide = useCallback(
    async (art: 'frei' | 'ab', grundCode?: Ablehnungsgrund | null) => {
      if (!aktuell || laeuft) return
      setLaeuft(true)
      setFehler(null)
      try {
        const ergebnis =
          art === 'frei'
            ? await aktion.freigeben(aktuell)
            : await aktion.ablehnen(aktuell, grundCode ?? null)
        setBericht(aktion.bericht(aktuell, ergebnis, art))
        setFragtGrund(false)
        neuLaden()
        // Damit die Zahlen in der Seitenleiste nicht stehenbleiben.
        etwasEntschieden()
      } catch (f) {
        setFehler(
          f instanceof ApiFehler
            ? f.message
            : f instanceof Error
              ? f.message
              : 'Unbekannter Fehler',
        )
        // Bei 409 hat jemand anderes entschieden — Liste auffrischen.
        if (f instanceof ApiFehler && f.status === 409) neuLaden()
      } finally {
        setLaeuft(false)
      }
    },
    [aktuell, aktion, laeuft, neuLaden],
  )

  useEffect(() => {
    function beiTaste(ereignis: KeyboardEvent) {
      // In einem Eingabefeld gehören Buchstaben dem Feld.
      const ziel = ereignis.target as HTMLElement | null
      const imFeld =
        ziel?.tagName === 'INPUT' ||
        ziel?.tagName === 'TEXTAREA' ||
        ziel?.isContentEditable

      if (ereignis.key === 'Escape') {
        if (fragtGrund) setFragtGrund(false)
        else if (bericht || fehler) {
          setBericht(null)
          setFehler(null)
        }
        return
      }

      if (imFeld || ereignis.metaKey || ereignis.ctrlKey || ereignis.altKey) return

      switch (ereignis.key) {
        case 'j':
        case 'ArrowDown':
          ereignis.preventDefault()
          setIndex((i) => Math.min(i + 1, eintraege.length - 1))
          break
        case 'k':
        case 'ArrowUp':
          ereignis.preventDefault()
          setIndex((i) => Math.max(i - 1, 0))
          break
        case 'a':
          if (!fragtGrund) {
            ereignis.preventDefault()
            void entscheide('frei')
          }
          break
        case 'r':
          if (!fragtGrund) {
            ereignis.preventDefault()
            // Ohne Gründe gibt es nichts zu fragen (Meldungen).
            if (gruende.length === 0) void entscheide('ab')
            else setFragtGrund(true)
          }
          break
      }
    }

    window.addEventListener('keydown', beiTaste)
    return () => window.removeEventListener('keydown', beiTaste)
  }, [eintraege.length, entscheide, fragtGrund, bericht, fehler, gruende.length])

  // Die gewählte Zeile in Sicht halten.
  useEffect(() => {
    const kind = listeRef.current?.children[index]
    kind?.scrollIntoView({ block: 'nearest' })
  }, [index])

  return (
    <section className="stapel">
      <div className="stapel-kopf">
        <h1>{titel}</h1>
        <p className="gedaempft zahl">
          {eintraege.length === 0
            ? 'nichts offen'
            : `${index + 1} von ${eintraege.length}`}
          {gesamt !== undefined && gesamt > eintraege.length && (
            <span className="leise"> · {gesamt} warten, der Rest rückt nach</span>
          )}
        </p>
      </div>

      {bericht && (
        <p className="meldung meldung-erfolg" role="status">
          {bericht}
        </p>
      )}
      {fehler && (
        <p className="meldung meldung-fehler" role="alert">
          {fehler}
        </p>
      )}

      {eintraege.length === 0 ? (
        <div className="karte">
          <p className="gedaempft">{leerText}</p>
        </div>
      ) : (
        <div className="stapel-koerper">
          <ul className="stapel-liste" ref={listeRef}>
            {eintraege.map((eintrag, i) => (
              <li key={schluessel(eintrag)}>
                <button
                  type="button"
                  className={i === index ? 'stapel-zeile aktiv' : 'stapel-zeile'}
                  aria-current={i === index}
                  onClick={() => setIndex(i)}
                >
                  {zeile(eintrag, i === index)}
                </button>
              </li>
            ))}
          </ul>

          <div className="stapel-inhalt">
            {aktuell && (
              <>
                {inhalt(aktuell)}

                <div className="stapel-fuss">
                  <button
                    className="knopf knopf-frei"
                    type="button"
                    disabled={laeuft || fragtGrund}
                    onClick={() => void entscheide('frei')}
                  >
                    {freigebenText} <kbd>a</kbd>
                  </button>
                  <button
                    className="knopf knopf-ab"
                    type="button"
                    disabled={laeuft || fragtGrund}
                    onClick={() =>
                      gruende.length === 0
                        ? void entscheide('ab')
                        : setFragtGrund(true)
                    }
                  >
                    {ablehnenText} <kbd>r</kbd>
                  </button>
                  <p className="leise stapel-hilfe">
                    <kbd>j</kbd> <kbd>k</kbd> blättern · <kbd>Esc</kbd> schließt
                  </p>
                </div>

                {fragtGrund && (
                  <Begruendung
                    laeuft={laeuft}
                    gruende={gruende}
                    abbrechen={() => setFragtGrund(false)}
                    absenden={(code) => void entscheide('ab', code)}
                  />
                )}
              </>
            )}
          </div>
        </div>
      )}
    </section>
  )
}
